import type { GetStaticPropsContext } from 'next';

import type { Bundle, Item, YotpoAlbumWithImages } from 'types';
import { getCategoryFromSlug, getCollection, getMarketingCopy, getPageSEO } from '@root/server/services/Collection';
import {
  findSanityBundleById,
  findSanityProductByCatalogNumber,
  getProductPageSettings,
} from '@root/server/services/Sanity';
import { getAlbumById } from '@root/server/services/Yotpo';
import type { YotpoUgcGallery as YotpoUgcGallerySchema } from '@root/sanity/sanity.types';
import { formatProductAssociations } from 'components/Collection/pages/product/AssociatedProducts';
import productCategories, {
  type CategoryDtoWithSlug,
  isValidSubcategory,
} from 'components/Collection/pages/categories';
import CategoryPage, { type CategoryPageProps } from 'components/Collection/pages/category';
import ProductPage, { type Props as ProductPageProps } from 'components/Collection/pages/product';
import { getInitialProductReviews } from 'components/Collection/pages/product/ProductReviews';
import { getProductImagesSortedByLabel, itemHasMedia } from 'components/Collection/pages/product/utils/media';
import { getItemPageSchema, getSubcategoryPageSchema } from 'data/PageSchema';
import { withPageStaticProps } from 'utils/header';
import { getItemUrl, getSEO, getVisualizerQueryString, isRegularObject } from 'utils/utils';
import {
  getBundleBySlug,
  getBundles,
  getProductBySlug,
  getProducts,
  getProductAssociations,
  getSurroundingProductsInCategory,
} from 'services/productsApi';

type Path = {
  /**
   * The URL slug of the product or subcategory page represented by the path
   */
  slug: string;

  /**
   * Represents the URL slug of the category that the product or subcategory belongs to
   * (e.g. `tuxedos-and-suits`, `accessories`)
   *
   * @note This is distinct from the categories assigned to items, which come from the CMS
   */
  category: string;
};

type PathParams = { params: Path };

type Context = GetStaticPropsContext & PathParams;

type PageProps = (ProductPageProps & { pageType: 'product' }) | (CategoryPageProps & { pageType: 'subcategory' });

export const getStaticPaths = async () => {
  const categories = productCategories.filter((c): c is CategoryDtoWithSlug => c.slug !== null);

  const paths = await categories.reduce(async (acc, category) => {
    const isBundle = category.slug.toLowerCase() === 'tuxedos-and-suits';
    const response = isBundle ? await getBundles() : await getProducts(category.name);
    const items = await response.json();
    const subcategories = category.subcategories ?? [];

    const itemsWithMedia = items.filter((item) => itemHasMedia(item));

    const productPages = itemsWithMedia.map(
      (item): PathParams => ({
        params: {
          slug: item.url_slug,
          category: category.slug,
        },
      })
    );

    const subcategoryPages = subcategories.map(
      (subcategory): PathParams => ({
        params: {
          slug: subcategory.slug,
          category: category.slug,
        },
      })
    );

    return (await acc).concat([...subcategoryPages, ...productPages]);
  }, Promise.resolve([] as PathParams[]));

  return { paths, fallback: false };
};

const getStaticPropsByPageType = async (context: Context) => {
  const { category, slug } = context.params;

  if (isValidSubcategory(category, slug)) {
    return await getSubcategoryPageProps(context);
  }

  return await getProductPageProps(context);
};

export const getStaticProps = withPageStaticProps(getStaticPropsByPageType);

const Page = (props: PageProps) => {
  switch (props.pageType) {
    case 'product':
      return <ProductPage {...(props as ProductPageProps)} />;
    case 'subcategory':
      return <CategoryPage {...(props as CategoryPageProps)} />;
  }
};

const getSubcategoryPageProps = async ({ params }: Context) => {
  const { category, slug } = params;

  const parentCategory = getCategoryFromSlug(category);
  const subcategory = parentCategory?.subcategories?.find((s) => s.slug === slug);

  const subcategorySlug = `collection/${category}/${slug}`;

  if (!subcategory) {
    throw new Error(`Failed to find a category and/or subcategory entry for /${subcategorySlug}`);
  }

  const { title = subcategory.displayName, metaDescription = '' } = (await getPageSEO(subcategorySlug)) ?? {};

  const url = `${process.env.NEXT_PUBLIC_LOCAL_URL}/${subcategorySlug}`;

  const products = await getCollection(parentCategory!.name);
  const filteredProducts = products.filter(subcategory.itemFilter);

  return {
    props: {
      pageType: 'subcategory',
      schema: getSubcategoryPageSchema(title, url, filteredProducts),
      seo: {
        title,
        metaDescription,
      },
      activeCategory: {
        name: parentCategory?.name,
        displayName: subcategory.displayName,
        slug: category,
      },
      products: filteredProducts,
      marketingCopy: await getMarketingCopy(subcategorySlug),
    },
  };
};

const getProductPageProps = async ({ params }: Context) => {
  const { category, slug } = params;

  const isBundle = category.toLowerCase() === 'tuxedos-and-suits';

  const getItemBySlug = isBundle ? getBundleBySlug : getProductBySlug;
  const response = await getItemBySlug(slug);

  if (response.status !== 200 && response.status !== 201) {
    throw new Error(
      `[/collection/${category}/${slug}] Got HTTP ${response.status} (${response.statusText}) fetching the item for this page`
    );
  }

  const product = await response.json();

  if (!isRegularObject(product)) {
    throw new Error(`[/collection/${category}/${slug}] Expected an item object but got ${typeof product}`);
  }

  const productAssociations = await (await getProductAssociations((product as Bundle).id)).json();
  const bundles = await (await getBundles()).json();
  const productAssociationsWithImages = formatProductAssociations(bundles, productAssociations);

  if (!isBundle && pathHasInvalidCategory(product, category)) {
    throw new Error(`[/collection/${category}/${slug}] Got unexpected product category: ${product.category}`);
  }

  let marketingCopy = null;
  let yotpoUgcGallery: YotpoUgcGallerySection | null = null;
  const productUrl = getItemUrl(product);
  const seoData = await getSEO(productUrl);

  if (!seoData?.metaDescription) {
    seoData.metaDescription = `${product.display_name} | ${product.short_description}`;
  }

  if (isBundle) {
    const sanityBundle = await findSanityBundleById(product.id.toString());

    marketingCopy = sanityBundle?.marketingCopyContentRaw ?? null;

    if (sanityBundle?.pageSeoTitle?.trim()) {
      seoData.title = sanityBundle.pageSeoTitle;
    }

    if (sanityBundle?.pageSeoDescription?.trim()) {
      seoData.metaDescription = sanityBundle.pageSeoDescription;
    }

    if (sanityBundle?.yotpoUgcGallery) {
      const { header, album } = sanityBundle.yotpoUgcGallery;

      const { images } = await getAlbumById(album);

      yotpoUgcGallery = {
        images,
        ...(header ? { header } : {}),
      };
    }
  } else {
    const sanityProduct = await findSanityProductByCatalogNumber(product.sku!);

    marketingCopy = sanityProduct?.marketingCopyContentRaw ?? null;

    if (sanityProduct?.pageSeoTitle) {
      seoData.title = sanityProduct.pageSeoTitle;
    }

    if (sanityProduct?.pageSeoDescription) {
      seoData.metaDescription = sanityProduct.pageSeoDescription;
    }
  }

  const { highlightedPromoCode } = await getProductPageSettings();

  return {
    props: {
      highlightedPromoCode,
      images: getProductImagesSortedByLabel(product),
      isBundle,
      marketingCopy,
      pageType: 'product',
      product,
      productAssociationsWithImages,
      reviews: getInitialProductReviews(product, isBundle),
      schema: await getItemPageSchema(product, productUrl),
      seo: seoData,
      sku: getItemSku(product, isBundle),
      surroundingProducts: await getSurroundingProductsInCategory(product),
      visualizerQueryString: getVisualizerQueryString(product, isBundle),
      yotpoUgcGallery,
    },
  };
};

type YotpoUgcGallerySection = Pick<YotpoUgcGallerySchema, 'header'> & Pick<YotpoAlbumWithImages, 'images'>;

const getItemSku = (product: Item, isBundle: boolean) => {
  if (isBundle) {
    return (product as Bundle).products!.map((p) => p.sku).join(',');
  }

  return product.sku;
};

const pathHasInvalidCategory = (product: Item, categoryFromPath: string) => {
  const productCategory = (product.category ?? '').toLowerCase();

  const invalidNonAccessoryCategory = !categoryFromPath.includes(productCategory);

  const invalidAccessoryCategory = !(
    productCategory.includes('belt') ||
    productCategory.includes('cufflinks') ||
    productCategory.includes('suspenders') ||
    productCategory.includes('pocket') ||
    productCategory.includes('lapel') ||
    productCategory.includes('bar')
  );

  if (categoryFromPath !== 'accessories') {
    return invalidNonAccessoryCategory;
  }

  return invalidAccessoryCategory;
};

export default Page;
